/**
 * Vessel prototypes — one built mesh per vessel kind, instanced per spawn.
 *
 * Each hull is authored to the exact dimensions in content/vessels.ts, so what the
 * simulation collides against and what the player sees are the same object. Local space
 * puts the waterline at Y=0 and the bow at +Z, which means a vessel is positioned by
 * dropping it straight onto the wave height the sim reports — no per-type fudge offsets.
 *
 * Silhouette is the priority. At 3 km through haze a ship is a shape and nothing else, so
 * the shapes are what these spend their polygons on: a tanker's long low deck and blocky
 * aft house, a supply vessel's forward bridge and open working deck, a skiff's near-flat
 * profile broken only by an outboard and a figure.
 */

import type { Scene } from '@babylonjs/core/scene';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';

import {
  HullAccumulator, addHull, addSuperstructure, addBulwark, addRailing,
  box, cylinder, taper, frustum, curvedTube, xform, member, type Rgb, type VesselMaterials,
} from './hullBuilder';
import { VESSEL_SPECS } from '../../content/vessels';
import {
  KRAKEN_VISUAL_BEAM,
  KRAKEN_VISUAL_HEIGHT,
  KRAKEN_VISUAL_LENGTH,
  KRAKEN_TENTACLE_VISUAL_HEIGHT,
  tentaclePathPoint,
  tentacleRadius,
} from '../../content/creatureScale';
import type { VesselKind } from '../../sim/types';

/*
 * The creature's dimensions and its authored limb curve now live in
 * `content/creatureScale.ts`, because the simulation has to collide against exactly what
 * is built here and cannot import a render module. Re-exported so the rest of the
 * renderer still asks the mesh layer how big the animal is.
 */
export {
  KRAKEN_VISUAL_LENGTH,
  KRAKEN_VISUAL_BEAM,
  KRAKEN_VISUAL_HEIGHT,
  KRAKEN_TENTACLE_VISUAL_HEIGHT,
};
const KRAKEN_ANATOMY_BEAM = 440;

/** Authored centreline shared by the builder and the target-reaching deformation. */
export function krakenTentaclePathPoint(t: number): Vector3 {
  const p = tentaclePathPoint(t);
  return new Vector3(p.x, p.y, p.z);
}

const KRAKEN_ARM_ANGLES = [-1.02, -0.76, -0.28, 0.42, 0.78, 1.62, 2.28, 2.68] as const;
const KRAKEN_ARM_LENGTHS = [610, 390, 710, 455, 735, 480, 680, 520] as const;
// Only arms 0 and 6 own the skyline. The other six are deliberately broad, low sweeps:
// they surface as interrupted muscular backs and sucker glimpses before the sea crops
// them again. Four equal raised arches made the animal read as a radial spider.
const KRAKEN_ARM_LIFT_A = [258, -54, 42, -66, 24, -72, 196, -48] as const;
const KRAKEN_ARM_LIFT_B = [454, -18, 92, -40, 72, -34, 334, 58] as const;
const KRAKEN_ARM_END_HEIGHTS = [122, -42, -46, -48, -58, -44, 168, -36] as const;
const KRAKEN_ARM_BENDS = [-270, 94, -304, 122, 292, -138, 168, -196] as const;
const KRAKEN_ARM_END_REACH = [0.40, 1.20, 0.68, 1.18, 1.05, 1.16, 0.70, 1.02] as const;
const KRAKEN_ARM_ROOT_X = [-174, -112, 76, 34, 142, 166, -18, -36] as const;
const KRAKEN_ARM_ROOT_Z = [86, 172, 232, 246, 176, 82, 8, 26] as const;
const KRAKEN_ARM_ROOT_RADII = [60, 41, 68, 36, 57, 39, 54, 33] as const;
const KRAKEN_ARM_TIP_RADII = [7.2, 4.1, 9.2, 3.8, 6.4, 4.0, 7.8, 4.6] as const;
/**
 * Point on each limb that churns the surface.
 *
 * The two skyline limbs meet the sea near their roots. The six low limbs use their
 * shallowest late curve, which keeps pressure rings on the visible sweep instead of
 * projecting a submerged tip's position onto unrelated water.
 */
export const KRAKEN_ARM_CONTACT_PROGRESS = [
  0.08, 0.62, 0.82, 0.64, 0.81, 0.64, 0.08, 0.80,
] as const;

/**
 * Palettes. Working ships are painted, and paint at sea is always going off.
 *
 * These run deliberately dark. Under the overcast this game lives in, the sea and sky are
 * bright and every vessel on them reads as a *silhouette* — that contrast is the whole
 * mechanism by which a hull resolves out of the haze at range (plan C9). Mid-grey hulls
 * were the first attempt and they vanished: at 600 m a skiff simply was not there. The
 * white accommodation block is the deliberate exception, because a bright superstructure
 * over a dark hull is exactly how a real ship reads at sea.
 */
const PALETTE = {
  tankerHull: [0.20, 0.075, 0.06] as Rgb,     // oxide red topsides, weathered near-black
  tankerBoot: [0.05, 0.05, 0.06] as Rgb,      // black boot-top below the waterline
  tankerDeck: [0.16, 0.17, 0.16] as Rgb,
  house: [0.62, 0.63, 0.62] as Rgb,           // off-white accommodation — the bright cue
  window: [0.02, 0.03, 0.04] as Rgb,
  supplyHull: [0.08, 0.12, 0.17] as Rgb,      // North Sea blue, nearly black at range
  supplyBoot: [0.05, 0.05, 0.05] as Rgb,
  workDeck: [0.14, 0.13, 0.11] as Rgb,
  skiffHull: [0.62, 0.18, 0.055] as Rgb,      // oxidised orange-red: small hull must clear dark water
  skiffTrim: [0.08, 0.075, 0.07] as Rgb,
  skiffDeck: [0.38, 0.22, 0.09] as Rgb,
  steel: [0.30, 0.31, 0.32] as Rgb,
  darkSteel: [0.11, 0.12, 0.12] as Rgb,
  rust: [0.21, 0.12, 0.07] as Rgb,
  hazard: [0.52, 0.36, 0.07] as Rgb,
  naval: [0.18, 0.21, 0.22] as Rgb,
  navalDark: [0.09, 0.11, 0.12] as Rgb,
  hostile: [0.11, 0.115, 0.11] as Rgb,
  hostilePatch: [0.23, 0.16, 0.10] as Rgb,
  lamp: [0.9, 0.9, 0.82] as Rgb,
} as const;

export interface VesselPrototypes {
  meshes: Map<VesselKind, Mesh>;
  /** Higher-detail creature meshes, drawn only inside the 1.5 km inspection envelope. */
  closeMeshes: Map<VesselKind, Mesh>;
  /** Scene estimate while the cheaper creature LOD is active. */
  farTriangleCount: number;
  /** Scene estimate while the close inspection creature LOD is active. */
  closeTriangleCount: number;
  dispose: () => void;
}

export type KrakenCloseDetail = 'desktop' | 'mobile';

/**
 * Build every vessel prototype once. Each returned mesh is disabled and never rendered
 * itself — it exists purely as the geometry source that instances point at.
 */
export function buildVesselPrototypes(
  scene: Scene,
  materials: VesselMaterials,
  closeDetail: KrakenCloseDetail = 'desktop',
): VesselPrototypes {
  const meshes = new Map<VesselKind, Mesh>();
  const closeMeshes = new Map<VesselKind, Mesh>();
  let baseTriangleCount = 0;
  let farCreatureTriangleCount = 0;
  let closeCreatureTriangleCount = 0;

  const builders: Record<VesselKind, (acc: HullAccumulator) => void> = {
    tanker: buildTanker,
    supply: buildSupply,
    skiff: buildSkiff,
    gunboat: buildGunboat,
    fac: buildFac,
    corvette: buildCorvette,
    minisub: buildMinisub,
    tentacle: buildTentacle,
    kraken: buildKraken,
  };

  for (const kind of Object.keys(builders) as VesselKind[]) {
    const acc = new HullAccumulator();
    (builders[kind])(acc);
    const mesh = acc.build(
      scene,
      `vessel_${kind}`,
      materials,
      kind === 'tentacle' || kind === 'kraken',
    );
    mesh.setEnabled(false);
    // Instances are positioned every frame; skipping the bounding refresh saves the
    // per-instance world-matrix recompute we would otherwise never use.
    mesh.alwaysSelectAsActiveMesh = true;
    meshes.set(kind, mesh);
    baseTriangleCount += acc.triangleCount;
    if (kind === 'tentacle' || kind === 'kraken') {
      farCreatureTriangleCount += acc.triangleCount;
    }
  }

  for (const kind of ['tentacle', 'kraken'] as const) {
    const acc = new HullAccumulator();
    if (kind === 'tentacle') buildTentacle(acc, closeDetail);
    else buildKraken(acc, closeDetail);
    const mesh = acc.build(
      scene,
      `vessel_${kind}_close_${closeDetail}`,
      materials,
      true,
    );
    mesh.setEnabled(false);
    mesh.alwaysSelectAsActiveMesh = true;
    closeMeshes.set(kind, mesh);
    closeCreatureTriangleCount += acc.triangleCount;
  }

  return {
    meshes,
    closeMeshes,
    farTriangleCount: baseTriangleCount,
    closeTriangleCount:
      baseTriangleCount - farCreatureTriangleCount + closeCreatureTriangleCount,
    dispose: () => {
      for (const m of [...meshes.values(), ...closeMeshes.values()]) {
        const multi = m.material;
        m.dispose();
        multi?.dispose(false, false);
      }
      meshes.clear();
      closeMeshes.clear();
    },
  };
}

/** Navigation lights remain useful after colour has vanished from the hull in darkness. */
function addNavLights(
  acc: HullAccumulator,
  halfBeam: number,
  y: number,
  z: number,
  size = 0.28,
): void {
  acc.add(
    cylinder(6),
    xform(-halfBeam, y, z, size, size * 1.6, size),
    PALETTE.lamp,
    'navRed',
  );
  acc.add(
    cylinder(6),
    xform(halfBeam, y, z, size, size * 1.6, size),
    PALETTE.lamp,
    'navGreen',
  );
}

function addRadarBar(
  acc: HullAccumulator,
  y: number,
  z: number,
  width: number,
): void {
  acc.add(box(), xform(0, y, z, width, 0.22, 0.62), PALETTE.steel, 'metal');
  acc.add(cylinder(6), xform(0, y - 1.3, z, 0.22, 2.6, 0.22), PALETTE.steel, 'metal');
}

// --- Tanker ------------------------------------------------------------------------------

function buildTanker(acc: HullAccumulator): void {
  const spec = VESSEL_SPECS.tanker;
  const L = spec.length;
  const B = spec.beam;
  const draft = spec.draft;
  const freeboard = 8.5;
  const deckY = freeboard;
  const half = L * 0.5;

  addHull(acc, {
    length: L, beam: B, draft, freeboard,
    sheer: 2.2,
    hullColor: PALETTE.tankerHull,
    bootTopColor: PALETTE.tankerBoot,
    bowFraction: 0.22,
    segments: 24,
    camber: 0.022,
    bowRake: 5.2,
    sternWidth: 0.78,
    flare: 0.92,
  });

  acc.add(
    box(),
    xform(0, deckY + 0.18, 0, B * 0.90, 0.34, L * 0.72),
    PALETTE.tankerDeck,
    'deck',
  );

  acc.add(
    frustum(0.68, 0.72, 0.08),
    xform(0, deckY + 1.9, half - 16, B * 0.70, 3.8, 25),
    PALETTE.tankerHull,
    'paint',
  );
  addRailing(acc, -B * 0.30, half - 6, B * 0.30, half - 6, deckY + 3.9, 1.05, PALETTE.steel);

  addSuperstructure(acc, {
    z: -half + 22, baseY: deckY, width: B * 0.66, depth: 17,
    levels: 5, levelHeight: 3.2, color: PALETTE.house, windowColor: PALETTE.window,
  });
  acc.add(
    frustum(0.78, 0.72, -0.05),
    xform(0, deckY + 17, -half + 13, 7.8, 11, 8.4),
    PALETTE.house,
    'paint',
  );
  acc.add(box(), xform(0, deckY + 23.1, -half + 13, 8, 1.6, 8.6), PALETTE.tankerBoot, 'rubber');
  acc.add(cylinder(8), xform(0, deckY + 20, -half + 27, 0.42, 7, 0.42), PALETTE.steel, 'metal');
  addRadarBar(acc, deckY + 23.7, -half + 27, 4.8);

  for (const x of [-B * 0.39, B * 0.39]) {
    acc.add(
      taper(0.72, 0.72, 10),
      xform(x, deckY + 9.2, -half + 20, 2.1, 7.5, 2.1, 0, Math.PI / 2, 0),
      PALETTE.hazard,
      'paint',
    );
  }

  for (let i = -3; i <= 3; i++) {
    const x = i * 2.6;
    const a = new Vector3(x, deckY + 1.5, -half + 34);
    const b = new Vector3(x, deckY + 1.5, half - 20);
    acc.add(
      cylinder(8),
      member(a, b, 0.55),
      i === 0 ? PALETTE.hazard : PALETTE.steel,
      'metal',
    );
  }

  acc.add(box(), xform(0, deckY + 2.6, 8, 13, 4.4, 7), PALETTE.darkSteel, 'metal');
  for (let i = -2; i <= 2; i++) {
    acc.add(
      cylinder(8),
      xform(i * 2.8, deckY + 5.4, 8, 0.9, 2.4, 0.9),
      PALETTE.hazard,
      'metal',
    );
  }

  for (let i = 0; i < 6; i++) {
    const z = -half + 44 + i * 17;
    acc.add(cylinder(10), xform(-B * 0.28, deckY + 1.1, z, 2.6, 1.6, 2.6), PALETTE.steel, 'metal');
    acc.add(cylinder(10), xform(B * 0.28, deckY + 1.1, z, 2.6, 1.6, 2.6), PALETTE.steel, 'metal');
    acc.add(box(), xform(0, deckY + 1.0, z, 1.6, 1.6, 1.6), PALETTE.darkSteel, 'metal');
  }

  for (const z of [-half + 60, -half + 110]) {
    acc.add(cylinder(8), xform(-B * 0.4, deckY + 5, z, 0.7, 10, 0.7), PALETTE.steel, 'metal');
    acc.add(cylinder(8), xform(B * 0.4, deckY + 5, z, 0.7, 10, 0.7), PALETTE.steel, 'metal');
    acc.add(
      cylinder(6),
      member(
        new Vector3(-B * 0.4, deckY + 9.5, z),
        new Vector3(B * 0.4, deckY + 9.5, z),
        0.22,
      ),
      PALETTE.steel,
      'metal',
    );
  }

  addBulwark(acc, { length: L * 0.9, beam: B * 0.99, deckY, height: 1.3, color: PALETTE.tankerHull });

  const wingY = deckY + 3.2 * 4.2;
  acc.add(box(), xform(-B * 0.42, wingY, -half + 22, 5, 0.5, 4), PALETTE.house, 'paint');
  acc.add(box(), xform(B * 0.42, wingY, -half + 22, 5, 0.5, 4), PALETTE.house, 'paint');
  addNavLights(acc, B * 0.43, wingY + 1.1, -half + 22, 0.34);
}

// --- Supply vessel -------------------------------------------------------------------------

function buildSupply(acc: HullAccumulator): void {
  const spec = VESSEL_SPECS.supply;
  const L = spec.length;
  const B = spec.beam;
  const draft = spec.draft;
  const freeboard = 5;
  const deckY = freeboard;
  const half = L * 0.5;

  addHull(acc, {
    length: L, beam: B, draft, freeboard,
    sheer: 2.4,
    hullColor: PALETTE.supplyHull,
    bootTopColor: PALETTE.supplyBoot,
    bowFraction: 0.26,
    segments: 20,
    camber: 0.02,
    bowRake: 4.2,
    sternWidth: 0.88,
    flare: 0.90,
  });

  // A forward, raked house leaves the whole afterbody as an unmistakable working deck.
  const houseZ = half - 18;
  acc.add(
    frustum(0.84, 0.78, 0.08),
    xform(0, deckY + 3.4, houseZ, B * 0.84, 6.8, 15),
    PALETTE.house,
    'paint',
  );
  acc.add(
    frustum(0.72, 0.64, 0.16),
    xform(0, deckY + 8.2, houseZ + 1.4, B * 0.68, 3.4, 10),
    PALETTE.house,
    'paint',
  );
  acc.add(
    box(),
    xform(0, deckY + 8.5, houseZ + 6.48, B * 0.55, 1.05, 0.14),
    PALETTE.window,
    'glass',
  );
  for (const x of [-B * 0.345, B * 0.345]) {
    acc.add(
      box(),
      xform(x, deckY + 8.45, houseZ + 1.3, 0.14, 0.95, 7.2),
      PALETTE.window,
      'glass',
    );
  }

  acc.add(
    box(),
    xform(0, deckY + 0.18, -half + 21, B * 0.88, 0.36, L * 0.50),
    PALETTE.workDeck,
    'deck',
  );
  addBulwark(acc, { length: L * 0.62, beam: B * 0.95, deckY, height: 1.5, color: PALETTE.supplyHull });

  const cargo: Array<[number, number, number, number, number, Rgb]> = [
    [-3.4, -12, 6, 2.6, 2.6, PALETTE.hazard],
    [3.4, -12, 6, 2.6, 2.6, PALETTE.rust],
    [-3.4, -22, 6, 2.6, 2.6, PALETTE.steel],
    [3.4, -22, 6, 1.7, 2.6, PALETTE.hazard],
  ];
  for (const [x, z, len, h, w, color] of cargo) {
    acc.add(box(), xform(x, deckY + h * 0.5 + 0.4, z, w, h, len), color, 'paint');
  }
  // Pipe bundles and two winch drums make the deck read as marine work, not a container
  // ship in miniature.
  for (const x of [-2.6, 0, 2.6]) {
    acc.add(
      cylinder(8),
      xform(x, deckY + 1.1, -31, 0.62, 7.5, 0.62, 0, Math.PI / 2, 0),
      PALETTE.steel,
      'metal',
    );
  }
  for (const x of [-3.2, 3.2]) {
    acc.add(
      cylinder(10),
      xform(x, deckY + 1.45, -4, 1.8, 2.2, 1.8, 0, 0, Math.PI / 2),
      PALETTE.darkSteel,
      'metal',
    );
  }

  const craneBase = new Vector3(B * 0.3, deckY + 1, -half + 34);
  acc.add(cylinder(10), xform(craneBase.x, deckY + 2.4, craneBase.z, 1.4, 4, 1.4), PALETTE.hazard, 'metal');
  acc.add(
    cylinder(8),
    member(
      new Vector3(craneBase.x, deckY + 4.4, craneBase.z),
      new Vector3(craneBase.x - 3, deckY + 12, craneBase.z - 9),
      0.42,
    ),
    PALETTE.hazard,
    'metal',
  );

  // Stern A-frame and roller: the offshore handling silhouette missing from the first
  // pass, now visible even when the deck cargo is lost in haze.
  const sternZ = -half + 5;
  for (const x of [-B * 0.38, B * 0.38]) {
    acc.add(
      cylinder(8),
      member(
        new Vector3(x, deckY + 0.8, sternZ),
        new Vector3(x * 0.62, deckY + 10.5, sternZ + 4),
        0.42,
      ),
      PALETTE.hazard,
      'metal',
    );
  }
  acc.add(
    cylinder(8),
    member(
      new Vector3(-B * 0.24, deckY + 10.5, sternZ + 4),
      new Vector3(B * 0.24, deckY + 10.5, sternZ + 4),
      0.38,
    ),
    PALETTE.hazard,
    'metal',
  );
  acc.add(
    cylinder(10),
    xform(0, deckY + 0.9, -half + 1.2, 1.4, B * 0.74, 1.4, 0, 0, Math.PI / 2),
    PALETTE.darkSteel,
    'metal',
  );

  addRadarBar(acc, deckY + 13.8, houseZ + 0.5, 4.2);
  addNavLights(acc, B * 0.38, deckY + 11.6, houseZ + 1.4);
  addRailing(acc, -B * 0.44, -half + 6, -B * 0.44, half - 28, deckY + 1.5, 1.05, PALETTE.steel);
  addRailing(acc, B * 0.44, -half + 6, B * 0.44, half - 28, deckY + 1.5, 1.05, PALETTE.steel);
}

// --- Pirate skiff ---------------------------------------------------------------------------

function buildSkiff(acc: HullAccumulator): void {
  const spec = VESSEL_SPECS.skiff;
  const L = spec.length;
  const B = spec.beam;
  const half = L * 0.5;

  addHull(acc, {
    length: L, beam: B, draft: spec.draft, freeboard: 1.34,
    sheer: 0.42,
    hullColor: PALETTE.skiffHull,
    bootTopColor: PALETTE.skiffTrim,
    // The shared large-ship paint scan is too coarse and dark at nine-metre scale.
    hullSurface: 'deck',
    bowFraction: 0.36,
    segments: 14,
    camber: 0,
    bowRake: 0.55,
    sternWidth: 0.92,
    openDeck: true,
  });

  acc.add(box(), xform(0, 0.78, -0.4, B * 0.67, 0.10, L * 0.62), PALETTE.skiffDeck, 'deck');

  // Thick workboat collars make the open hull's upper edge legible against both sky and
  // water. Without these continuous lines the tiny top profile dissolved into the sea.
  for (const x of [-B * 0.47, B * 0.47]) {
    acc.add(
      cylinder(8),
      member(new Vector3(x, 1.30, -half + 0.45), new Vector3(x, 1.43, half - 0.85), 0.34),
      PALETTE.skiffHull,
      'rubber',
    );
  }

  for (const z of [-1.6, 0.6, 2.4]) {
    acc.add(box(), xform(0, 1.04, z, B * 0.72, 0.12, 0.34), PALETTE.hostilePatch, 'paint');
  }

  // Twin outboards make the stern broad and fast-looking even before the wake appears.
  for (const x of [-0.42, 0.42]) {
    acc.add(box(), xform(x, 1.12, -half + 0.18, 0.42, 0.88, 0.52), PALETTE.darkSteel, 'metal');
    acc.add(box(), xform(x, 0.25, -half - 0.02, 0.19, 0.78, 0.24), PALETTE.darkSteel, 'metal');
  }

  acc.add(box(), xform(-0.25, 1.30, 0.75, 0.52, 0.58, 0.34), PALETTE.darkSteel, 'metal');
  // Driver standing aft; gunner crouched forward. Their unequal posture keeps the boat
  // from becoming a perfectly mirrored toy and provides an immediate human scale.
  acc.add(cylinder(6), xform(0.38, 1.60, -1.0, 0.34, 1.05, 0.34), [0.07, 0.065, 0.06], 'rubber');
  acc.add(cylinder(6), xform(0.38, 2.24, -1.0, 0.26, 0.26, 0.26), [0.22, 0.16, 0.13], 'organic');
  acc.add(cylinder(6), xform(-0.34, 1.42, 1.7, 0.34, 0.72, 0.34), [0.09, 0.08, 0.07], 'rubber');
  acc.add(cylinder(6), xform(-0.34, 1.89, 1.7, 0.25, 0.25, 0.25), [0.19, 0.13, 0.10], 'organic');

  // A visibly improvised light weapon on a pipe tripod.
  acc.add(cylinder(6), xform(0, 1.5, 2.55, 0.12, 0.9, 0.12), PALETTE.darkSteel, 'metal');
  acc.add(
    cylinder(6),
    member(new Vector3(0, 1.92, 2.45), new Vector3(0, 1.98, 3.65), 0.10),
    PALETTE.darkSteel,
    'metal',
  );

  acc.add(cylinder(8), xform(-0.58, 1.22, -0.2, 0.5, 0.76, 0.5), PALETTE.rust, 'metal');
  acc.add(cylinder(8), xform(0.58, 1.22, -2.1, 0.5, 0.76, 0.5), PALETTE.hazard, 'metal');
}

// --- Types introduced later, built now so the fleet is complete ------------------------------

function buildGunboat(acc: HullAccumulator): void {
  const spec = VESSEL_SPECS.gunboat;
  const L = spec.length, B = spec.beam, half = L * 0.5;
  const deckY = 2.15;

  addHull(acc, {
    length: L, beam: B, draft: spec.draft, freeboard: deckY,
    sheer: 1.15, hullColor: PALETTE.hostile, bootTopColor: PALETTE.skiffTrim,
    bowFraction: 0.34, segments: 18, bowRake: 1.45, sternWidth: 0.9, camber: 0.014,
  });

  acc.add(box(), xform(0, deckY + 0.14, -1.5, B * 0.88, 0.28, L * 0.66), PALETTE.workDeck, 'deck');

  // A high, rough pilothouse is the gunboat's recognition shape; it is deliberately
  // upright and workmanlike beside the FAC's low wedge.
  const cabinZ = -1.5;
  acc.add(
    frustum(0.78, 0.70, 0.12),
    xform(0, deckY + 2.05, cabinZ, B * 0.72, 4.1, 6.4),
    PALETTE.hostilePatch,
    'paint',
  );
  acc.add(
    box(),
    xform(0, deckY + 2.45, cabinZ + 3.3, B * 0.54, 0.82, 0.12),
    PALETTE.window,
    'glass',
  );
  for (const x of [-B * 0.365, B * 0.365]) {
    acc.add(
      box(),
      xform(x, deckY + 2.42, cabinZ + 0.2, 0.12, 0.76, 3.9),
      PALETTE.window,
      'glass',
    );
  }

  // The oversized shield and long barrel are readable well before surface detail.
  const gunZ = half - 4.6;
  acc.add(cylinder(8), xform(0, deckY + 0.72, gunZ, 1.05, 1.15, 1.05), PALETTE.darkSteel, 'metal');
  acc.add(
    frustum(0.62, 0.52, 0.14),
    xform(0, deckY + 1.55, gunZ, 1.9, 1.25, 1.75),
    PALETTE.hostilePatch,
    'metal',
  );
  acc.add(
    cylinder(8),
    member(
      new Vector3(0, deckY + 1.7, gunZ + 0.55),
      new Vector3(0, deckY + 1.85, gunZ + 4.0),
      0.14,
    ),
    PALETTE.darkSteel,
    'metal',
  );

  // Pipe-frame mast, radar bar and exposed exhausts complete the improvised silhouette.
  for (const x of [-1.25, 1.25]) {
    acc.add(
      cylinder(6),
      member(
        new Vector3(x, deckY + 3.2, cabinZ - 1.6),
        new Vector3(x * 0.4, deckY + 7.3, cabinZ - 0.3),
        0.16,
      ),
      PALETTE.steel,
      'metal',
    );
  }
  addRadarBar(acc, deckY + 7.3, cabinZ - 0.3, 3.2);
  for (const x of [-1.35, 1.35]) {
    acc.add(cylinder(8), xform(x, deckY + 1.4, -half + 3.6, 0.38, 2.6, 0.38), PALETTE.darkSteel, 'metal');
  }

  addRailing(acc, -B * 0.43, -half + 2, -B * 0.43, cabinZ - 3.5, deckY + 0.2, 0.82, PALETTE.steel);
  addRailing(acc, B * 0.43, -half + 2, B * 0.43, cabinZ - 3.5, deckY + 0.2, 0.82, PALETTE.steel);
  addNavLights(acc, B * 0.37, deckY + 4.4, cabinZ + 0.6, 0.20);
}

function buildFac(acc: HullAccumulator): void {
  const spec = VESSEL_SPECS.fac;
  const L = spec.length, B = spec.beam, half = L * 0.5;
  const deckY = 2.75;

  addHull(acc, {
    length: L, beam: B, draft: spec.draft, freeboard: deckY,
    sheer: 1.7, hullColor: PALETTE.navalDark, bootTopColor: PALETTE.skiffTrim,
    bowFraction: 0.38, segments: 20, bowRake: 2.2, sternWidth: 0.84,
    flare: 0.86, camber: 0.012,
  });

  acc.add(box(), xform(0, deckY + 0.12, -1.5, B * 0.9, 0.24, L * 0.66), PALETTE.navalDark, 'deck');

  // One continuous low wedge, a dark visor, and a swept mast create a speed-first shape
  // that cannot be mistaken for the gunboat's upright wheelhouse.
  const bridgeZ = 2.0;
  acc.add(
    frustum(0.48, 0.46, 0.24),
    xform(0, deckY + 2.0, bridgeZ, B * 0.76, 4.0, 11.5),
    PALETTE.naval,
    'paint',
  );
  acc.add(
    frustum(0.52, 0.48, 0.22),
    xform(0, deckY + 4.55, bridgeZ + 1.8, B * 0.52, 2.1, 6.4),
    PALETTE.naval,
    'paint',
  );
  acc.add(
    box(),
    xform(0, deckY + 4.72, bridgeZ + 5.05, B * 0.45, 0.72, 0.13),
    PALETTE.window,
    'glass',
  );
  for (const x of [-B * 0.265, B * 0.265]) {
    acc.add(
      box(),
      xform(x, deckY + 4.65, bridgeZ + 2.0, 0.12, 0.68, 4.2),
      PALETTE.window,
      'glass',
    );
  }

  // Paired missile boxes are the FAC's gameplay identity made visible.
  for (const x of [-B * 0.28, B * 0.28]) {
    const yaw = x < 0 ? -0.10 : 0.10;
    acc.add(
      frustum(0.82, 0.86, 0.02),
      xform(x, deckY + 1.45, -half + 8.5, 1.8, 2.4, 4.5, yaw),
      PALETTE.darkSteel,
      'metal',
    );
  }

  // Compact bow cannon.
  const gunZ = half - 6.0;
  acc.add(cylinder(10), xform(0, deckY + 0.65, gunZ, 1.3, 1.1, 1.3), PALETTE.naval, 'metal');
  acc.add(
    cylinder(8),
    member(
      new Vector3(0, deckY + 1.0, gunZ + 0.35),
      new Vector3(0, deckY + 1.25, gunZ + 3.9),
      0.13,
    ),
    PALETTE.darkSteel,
    'metal',
  );

  const mastBase = new Vector3(0, deckY + 5.2, bridgeZ - 1.6);
  const mastTop = new Vector3(0, deckY + 10.7, bridgeZ - 3.0);
  acc.add(cylinder(8), member(mastBase, mastTop, 0.28), PALETTE.steel, 'metal');
  acc.add(
    frustum(0.72, 0.48, 0),
    xform(0, deckY + 9.7, bridgeZ - 2.7, 3.4, 2.2, 0.42, 0, 0, -0.12),
    PALETTE.steel,
    'metal',
  );
  for (const x of [-1.6, 1.6]) {
    acc.add(cylinder(8), xform(x, deckY + 0.9, -half + 3.4, 0.44, 1.8, 0.44), PALETTE.darkSteel, 'metal');
  }

  addNavLights(acc, B * 0.34, deckY + 6.3, bridgeZ, 0.22);
}

function buildCorvette(acc: HullAccumulator): void {
  const spec = VESSEL_SPECS.corvette;
  const L = spec.length, B = spec.beam, half = L * 0.5;
  const deckY = 6;

  addHull(acc, {
    length: L, beam: B, draft: spec.draft, freeboard: deckY,
    sheer: 3.5, hullColor: PALETTE.naval, bootTopColor: PALETTE.tankerBoot,
    bowFraction: 0.34, segments: 26, bowRake: 4.8, sternWidth: 0.76,
    flare: 0.88, camber: 0.018,
  });

  acc.add(
    box(),
    xform(0, deckY + 0.15, -1.5, B * 0.90, 0.30, L * 0.70),
    [0.12, 0.14, 0.15],
    'deck',
  );

  // A coherent sloped citadel replaces the generic stack of accommodation boxes.
  const bridgeZ = 5.5;
  acc.add(
    frustum(0.68, 0.68, 0.10),
    xform(0, deckY + 3.2, bridgeZ, B * 0.78, 6.4, 24),
    PALETTE.naval,
    'paint',
  );
  acc.add(
    frustum(0.56, 0.58, 0.14),
    xform(0, deckY + 7.4, bridgeZ + 2.8, B * 0.60, 3.6, 13),
    PALETTE.naval,
    'paint',
  );
  acc.add(
    box(),
    xform(0, deckY + 7.7, bridgeZ + 9.45, B * 0.50, 0.88, 0.15),
    PALETTE.window,
    'glass',
  );
  for (const x of [-B * 0.305, B * 0.305]) {
    acc.add(
      box(),
      xform(x, deckY + 7.65, bridgeZ + 3.2, 0.14, 0.82, 7.6),
      PALETTE.window,
      'glass',
    );
  }

  // 76 mm gun forward — broad shield, long barrel, clean field of fire.
  const gunZ = half - 18;
  acc.add(cylinder(10), xform(0, deckY + 1.1, gunZ, 4.5, 2.0, 4.5), PALETTE.naval, 'metal');
  acc.add(
    frustum(0.58, 0.55, 0.16),
    xform(0, deckY + 2.45, gunZ, 4.2, 2.6, 5.0),
    PALETTE.naval,
    'metal',
  );
  acc.add(
    cylinder(8),
    member(
      new Vector3(0, deckY + 2.8, gunZ + 1.8),
      new Vector3(0, deckY + 4.0, gunZ + 11.2),
      0.28,
    ),
    PALETTE.darkSteel,
    'metal',
  );

  // Tripod mast and large planar radar give the corvette height and disciplined naval
  // order without making the whole superstructure tall.
  const mastZ = bridgeZ - 1;
  for (const x of [-2.1, 2.1]) {
    acc.add(
      cylinder(8),
      member(
        new Vector3(x, deckY + 7.7, mastZ - 1.4),
        new Vector3(0, deckY + 15.5, mastZ),
        0.30,
      ),
      PALETTE.steel,
      'metal',
    );
  }
  acc.add(cylinder(8), xform(0, deckY + 14.0, mastZ, 0.34, 7.0, 0.34), PALETTE.steel, 'metal');
  acc.add(
    frustum(0.72, 0.50, 0),
    xform(0, deckY + 15.1, mastZ + 0.2, 5.2, 3.0, 0.48, 0, 0, -0.10),
    PALETTE.steel,
    'metal',
  );
  addRadarBar(acc, deckY + 15.4, mastZ, 5.8);

  // Funnel, VLS block and stern hangar/flight deck complete a readable fore-to-aft
  // hierarchy: gun → bridge → mast → exhaust → aviation deck.
  acc.add(
    frustum(0.72, 0.62, -0.10),
    xform(0, deckY + 5.0, -10, 4.8, 8.0, 7.2, 0, 0, -0.08),
    PALETTE.navalDark,
    'metal',
  );
  acc.add(box(), xform(0, deckY + 1.0, -2.5, B * 0.48, 1.7, 7.0), PALETTE.darkSteel, 'metal');
  for (const x of [-2.1, -0.7, 0.7, 2.1]) {
    acc.add(box(), xform(x, deckY + 1.92, -2.5, 0.8, 0.14, 4.8), PALETTE.steel, 'metal');
  }
  acc.add(
    frustum(0.82, 0.82, -0.04),
    xform(0, deckY + 3.0, -24, B * 0.76, 6.0, 15),
    PALETTE.naval,
    'paint',
  );
  acc.add(box(), xform(0, deckY + 0.22, -half + 10, B * 0.88, 0.34, 18), [0.16, 0.17, 0.17], 'deck');
  // Minimal deck marking: large enough to survive the scope, restrained enough not to
  // turn the ship into a toy.
  acc.add(box(), xform(0, deckY + 0.42, -half + 10, B * 0.48, 0.05, 0.42), PALETTE.house, 'paint');
  acc.add(box(), xform(0, deckY + 0.42, -half + 10, 0.42, 0.05, 8), PALETTE.house, 'paint');

  addBulwark(acc, { length: L * 0.82, beam: B * 0.98, deckY, height: 1.15, color: PALETTE.naval });
  addNavLights(acc, B * 0.40, deckY + 10.1, bridgeZ + 2.8, 0.26);
}

/** A hooked, visibly organic limb whose vertices are further deformed at render time. */
function buildTentacle(
  acc: HullAccumulator,
  closeDetail?: KrakenCloseDetail,
): void {
  const segments = closeDetail === 'desktop' ? 56 : closeDetail === 'mobile' ? 42 : 20;
  const radialSides = closeDetail === 'desktop' ? 32 : closeDetail === 'mobile' ? 24 : 14;
  const suckerSides = closeDetail === 'desktop' ? 14 : closeDetail === 'mobile' ? 10 : 7;
  const suckerStep = closeDetail ? 1 : 2;
  const flesh: Rgb = [0.068, 0.102, 0.112];
  const underside: Rgb = [0.30, 0.145, 0.155];
  const suckerCavity: Rgb = [0.026, 0.012, 0.018];

  const points = Array.from(
    { length: segments + 1 },
    (_, i) => krakenTentaclePathPoint(i / segments),
  );
  const radii = Array.from({ length: segments + 1 }, (_, i) => tentacleRadius(i / segments));
  const tubeT: number[] = [];
  for (let slice = 0; slice < points.length; slice++) {
    const t = slice / (points.length - 1);
    for (let side = 0; side < radialSides; side++) tubeT.push(t);
  }
  tubeT.push(0, 1);
  acc.add(
    curvedTube(points, radii, radialSides, true),
    xform(0, 0, 0, 1, 1, 1),
    flesh,
    'organic',
    0.014,
    { group: 1, t: tubeT },
  );

  // Two irregular rows ride the inside of the hook. Alternating stations keep the cups
  // anatomical at combat distance without spending the Kraken's remaining triangle
  // budget on detail hidden behind the hull.
  for (let i = 2; i < segments; i += suckerStep) {
    const t = (i + 0.15) / segments;
    const p = krakenTentaclePathPoint(t);
    const radius = tentacleRadius(t);
    for (const side of [-1, 1]) {
      const rhythm = 0.88 + 0.18 * Math.sin(i * 2.31 + side * 0.7);
      const sucker = Math.max(0.58, radius * 0.41 * rhythm);
      const cupRoll = side > 0 ? -Math.PI / 2 : Math.PI / 2;
      acc.add(
        taper(0.58, 1, suckerSides),
        xform(
          p.x + side * radius * 0.92,
          p.y - radius * 0.10,
          p.z - radius * 0.34,
          sucker,
          sucker * 0.34,
          sucker,
          0,
          0,
          cupRoll,
        ),
        underside,
        'organic',
        0.014,
        { group: 1, t },
      );
      // A dark inset turns the raised cone into a readable suction cup. The previous
      // solid pink stud lost its opening at combat distance and read as a rivet.
      acc.add(
        taper(0.82, 1, Math.max(7, suckerSides - 4)),
        xform(
          p.x + side * (radius * 1.03 + sucker * 0.06),
          p.y - radius * 0.10,
          p.z - radius * 0.34,
          sucker * 0.58,
          sucker * 0.12,
          sucker * 0.58,
          0,
          0,
          cupRoll,
        ),
        suckerCavity,
        'organic',
        0.014,
        { group: 1, t },
      );
    }

    // A staggered third field faces inward toward the thing being gripped. With only
    // the two lateral rows, the rig-side view saw a perfectly smooth black wall at the
    // exact moment the limb crossed the occupied deck. Half-density keeps the spacing
    // organic and makes suckers readable from the player's side without turning the
    // whole circumference into armour studs.
    if (i % (closeDetail ? 2 : 4) === 0) {
      const face = new Vector3(0, -0.16, 0.987).normalize();
      const rhythm = 0.84 + 0.16 * Math.sin(i * 2.07 + 0.9);
      const sucker = Math.max(0.58, radius * 0.39 * rhythm);
      const drift = Math.sin(i * 1.73) * radius * 0.14;
      const opening = p
        .add(face.scale(radius * 0.97))
        .add(new Vector3(drift, 0, 0));
      const base = opening.subtract(face.scale(sucker * 0.34));
      acc.add(
        taper(0.58, 1, suckerSides),
        member(opening, base, sucker),
        underside,
        'organic',
        0.014,
        { group: 1, t },
      );
      const cavityA = opening.add(face.scale(0.05));
      const cavityB = opening.add(face.scale(sucker * 0.11));
      acc.add(
        taper(0.82, 1, Math.max(7, suckerSides - 4)),
        member(cavityA, cavityB, sucker * 0.58),
        suckerCavity,
        'organic',
        0.014,
        { group: 1, t },
      );
    }
  }
}

/** A softly irregular sphere, kept local because it is only used by the Kraken. */
function organicSpheroid(segments = 28, rings = 18): VertexData {
  const positions: number[] = [];
  const indices: number[] = [];

  for (let ring = 0; ring <= rings; ring++) {
    const v = ring / rings;
    const latitude = (v - 0.5) * Math.PI;
    const y = Math.sin(latitude) * 0.5;
    const circumference = Math.cos(latitude) * 0.5;

    for (let segment = 0; segment <= segments; segment++) {
      const u = segment / segments;
      const longitude = u * Math.PI * 2;
      // Broad, low-frequency folds break the perfect balloon highlight without making
      // the silhouette faceted.
      const fold = 1
        + Math.sin(longitude * 5 + latitude * 1.7) * 0.055
        + Math.sin(longitude * 2 - latitude * 3.1) * 0.034
        + Math.sin(longitude * 9 + latitude * 4.3) * 0.012;
      positions.push(
        Math.cos(longitude) * circumference * fold,
        y * (1 + Math.cos(longitude * 3) * 0.038),
        Math.sin(longitude) * circumference * fold,
      );
    }
  }

  const stride = segments + 1;
  for (let ring = 0; ring < rings; ring++) {
    for (let segment = 0; segment < segments; segment++) {
      const a = ring * stride + segment;
      const b = a + stride;
      indices.push(a, b, b + 1, a, b + 1, a + 1);
    }
  }

  const normals: number[] = [];
  VertexData.ComputeNormals(positions, indices, normals);
  const data = new VertexData();
  data.positions = positions;
  data.indices = indices;
  data.normals = normals;
  return data;
}

export interface KrakenArmPath {
  points: Vector3[];
  radii: number[];
}

function cubicArmPath(
  start: Vector3,
  controlA: Vector3,
  controlB: Vector3,
  end: Vector3,
  startRadius: number,
  endRadius: number,
  slices = 20,
): KrakenArmPath {
  const points: Vector3[] = [];
  const radii: number[] = [];
  for (let i = 0; i <= slices; i++) {
    const t = i / slices;
    const s = 1 - t;
    points.push(
      start.scale(s * s * s)
        .add(controlA.scale(3 * s * s * t))
        .add(controlB.scale(3 * s * t * t))
        .add(end.scale(t * t * t)),
    );
    radii.push(startRadius * Math.pow(s, 0.72) + endRadius * t);
  }
  return { points, radii };
}

export function createKrakenArmPath(armIndex: number, slices = 22): KrakenArmPath {
  const angle = KRAKEN_ARM_ANGLES[armIndex] as number;
  const outward = KRAKEN_ARM_LENGTHS[armIndex] as number;
  const root = new Vector3(
    KRAKEN_ARM_ROOT_X[armIndex] as number,
    -34,
    KRAKEN_ARM_ROOT_Z[armIndex] as number,
  );
  const alongX = Math.cos(angle);
  const alongZ = Math.sin(angle);
  const crossX = -alongZ;
  const crossZ = alongX;
  const bend = KRAKEN_ARM_BENDS[armIndex] as number;
  const controlA = new Vector3(
    root.x + alongX * outward * 0.37,
    KRAKEN_ARM_LIFT_A[armIndex] as number,
    root.z + alongZ * outward * 0.37,
  );
  const controlB = new Vector3(
    root.x + alongX * outward * 0.78 + crossX * bend,
    KRAKEN_ARM_LIFT_B[armIndex] as number,
    root.z + alongZ * outward * 0.78 + crossZ * bend,
  );
  const end = new Vector3(
    root.x + alongX * outward * (KRAKEN_ARM_END_REACH[armIndex] as number)
      + crossX * bend * 0.72,
    KRAKEN_ARM_END_HEIGHTS[armIndex] as number,
    root.z + alongZ * outward * (KRAKEN_ARM_END_REACH[armIndex] as number)
      + crossZ * bend * 0.72,
  );
  return cubicArmPath(
    root,
    controlA,
    controlB,
    end,
    KRAKEN_ARM_ROOT_RADII[armIndex] as number,
    KRAKEN_ARM_TIP_RADII[armIndex] as number,
    slices,
  );
}

/** Authored local-space surface contacts used by the foam response. */
export const KRAKEN_ARM_WATER_CONTACTS = KRAKEN_ARM_CONTACT_PROGRESS.map(
  (progress, armIndex) => {
    const path = createKrakenArmPath(armIndex);
    return path.points[Math.round(progress * (path.points.length - 1))] as Vector3;
  },
);

function addKrakenSuckers(
  acc: HullAccumulator,
  path: KrakenArmPath,
  armIndex: number,
  cupColor: Rgb,
  cavityColor: Rgb,
  stationStep = 2,
  radialSides = 6,
): void {
  const up = new Vector3(0, 1, 0);
  const fallback = new Vector3(0, 0, 1);

  // Two irregular rows turn a tube into a grasping surface. The cups grow toward the
  // root and are deliberately oversized: at the Kraken's authored scale an anatomically
  // modest sucker would disappear into one pixel before the player could read it.
  for (let i = 3; i < path.points.length - 2; i += stationStep) {
    const point = path.points[i] as Vector3;
    const previous = path.points[i - 1] as Vector3;
    const next = path.points[i + 1] as Vector3;
    const tangent = next.subtract(previous).normalize();
    let side = Vector3.Cross(tangent, up);
    if (side.lengthSquared() < 1e-5) side = Vector3.Cross(tangent, fallback);
    side.normalize();

    const radius = path.radii[i] as number;
    const inward = new Vector3(-point.x, -Math.max(55, point.y + 35) * 0.55, 210 - point.z)
      .normalize();
    const exposureVectors = [
      [0.78, 0.63], [0.35, 0.94], [0.84, 0.54], [-0.25, 0.97],
      [-0.72, 0.69], [-0.90, 0.35], [0.30, -0.95], [0.90, -0.30],
    ] as const;
    const exposure = exposureVectors[armIndex] as readonly [number, number];
    // Cephalopod arms roll along their length. Selected limbs turn their grasping face
    // toward different points around the animal. Several rows are legible from any
    // approach, but no two neighbouring limbs present the same manufactured underside.
    const face = new Vector3(exposure[0], -0.46, exposure[1])
      .add(inward.scale(0.12))
      .normalize();

    for (const row of [-1, 1]) {
      // Stagger the rows and drop an occasional cup. Perfect spacing looked machined;
      // living tissue needs gaps, drift and unequal sizes.
      if ((i + armIndex * 3 + (row > 0 ? 1 : 0)) % 11 === 0) continue;
      const stagger = row * radius * (0.27 + ((i + armIndex) % 3) * 0.025);
      const drift = Math.sin(i * 2.17 + armIndex * 1.31 + row) * radius * 0.055;
      const opening = point
        .add(face.scale(radius * 1.03))
        .add(side.scale(stagger + drift));
      const diameter = Math.max(2.8, radius * (0.44 + 0.045 * Math.sin(i * 1.7 + armIndex)));
      const depth = Math.max(1.4, diameter * 0.24);
      const base = opening.subtract(face.scale(depth));

      acc.add(
        taper(0.52, 1, radialSides),
        member(opening, base, diameter),
        cupColor,
        'organic',
        0.022,
        { group: armIndex + 1, t: i / (path.points.length - 1) },
      );

      // A dark disk just beyond the lip gives the cup visible depth. Without this the
      // taper cap catches light as a pale button and the whole field reads as rivets.
      const cavityA = opening.add(face.scale(0.10));
      const cavityB = opening.add(face.scale(Math.max(0.45, depth * 0.20)));
      acc.add(
        cylinder(radialSides),
        member(cavityA, cavityB, diameter * 0.62),
        cavityColor,
        'organic',
        0.026,
        { group: armIndex + 1, t: i / (path.points.length - 1) },
      );
    }
  }
}

/**
 * The Kraken is scenery that attacks, not a boss-shaped boat.
 *
 * Its surfaced mass is about a kilometre across and more than 300 m high: large enough
 * to dwarf the full oil rig in the same frame. The mantle, brow and arm crown overlap
 * without exposing joints, while the eight heavy arms disappear through the waterline so
 * the visible geometry never tells the player where the animal ends.
 */
function buildKraken(
  acc: HullAccumulator,
  closeDetail?: KrakenCloseDetail,
): void {
  const length = KRAKEN_VISUAL_LENGTH;
  const beam = KRAKEN_ANATOMY_BEAM;
  const flesh: Rgb = [0.068, 0.102, 0.112];
  const litFlesh: Rgb = [0.098, 0.145, 0.154];
  const dark: Rgb = [0.010, 0.019, 0.024];
  const ridge: Rgb = [0.034, 0.058, 0.064];
  const underside: Rgb = [0.22, 0.115, 0.125];
  const sclera: Rgb = [0.28, 0.34, 0.36];
  const eye: Rgb = [0.38, 0.24, 0.055];
  const pupil: Rgb = [0.006, 0.004, 0.005];
  const bodySegments = closeDetail === 'desktop' ? 64 : closeDetail === 'mobile' ? 42 : 22;
  const bodyRings = closeDetail === 'desktop' ? 36 : closeDetail === 'mobile' ? 24 : 12;
  const featureSegments = closeDetail === 'desktop' ? 40 : closeDetail === 'mobile' ? 30 : 14;
  const featureRings = closeDetail === 'desktop' ? 24 : closeDetail === 'mobile' ? 18 : 8;
  const armSlices = closeDetail === 'desktop' ? 60 : closeDetail === 'mobile' ? 42 : 22;
  const armSides = closeDetail === 'desktop' ? 30 : closeDetail === 'mobile' ? 22 : 12;
  const suckerStep = closeDetail === 'desktop' ? 1 : 2;
  const suckerSides = closeDetail === 'desktop' ? 14 : closeDetail === 'mobile' ? 12 : 6;
  const spheroid = organicSpheroid(bodySegments, bodyRings);
  const featureSpheroid = organicSpheroid(featureSegments, featureRings);
  const glintSpheroid = organicSpheroid(
    closeDetail === 'desktop' ? 24 : closeDetail === 'mobile' ? 18 : 10,
    closeDetail === 'desktop' ? 16 : closeDetail === 'mobile' ? 12 : 6,
  );

  // Bow is +Z for every vessel, including the creature. The crown leans to port and the
  // eye-side shoulder carries more mass, so the mantle reads as one resting animal
  // instead of a centred pod with appendages distributed around it.
  acc.add(
    spheroid,
    xform(-44, 28, -150, beam * 1.24, 470, length * 0.84, -0.02, 0.16, -0.07),
    flesh,
    'organic',
    0.0038,
  );
  acc.add(
    spheroid,
    xform(-106, 142, -322, beam * 0.70, 294, length * 0.46, -0.07, 0.24, -0.10),
    litFlesh,
    'organic',
    0.0038,
  );
  acc.add(
    spheroid,
    xform(66, 12, 176, beam * 1.04, 234, length * 0.42, 0, -0.10, 0.05),
    flesh,
    'organic',
    0.0038,
  );
  // One enormous lateral eye is enough. Most of the globe sits behind the cheek surface:
  // the animal presents a narrow wet lens through two unequal lid masses, not a sphere
  // mounted on its flank. No matching eye is placed on the far side.
  const eyeSide = 1;
  acc.add(
    featureSpheroid,
    xform(eyeSide * beam * 0.635, 34, 210, 36, 74, 110, -0.01, 0.01, 0.05),
    sclera,
    'pupil',
    0.012,
    { group: 9, t: 4 },
  );
  acc.add(
    featureSpheroid,
    xform(eyeSide * beam * 0.666, 34, 216, 12, 54, 74, -0.01, 0.01, 0.05),
    eye,
    'eye',
    0.012,
    { group: 9, t: 4 },
  );
  acc.add(
    featureSpheroid,
    xform(eyeSide * beam * 0.682, 34, 224, 8, 40, 5, -0.02, 0.01, 0.05),
    pupil,
    'pupil',
    0.012,
    { group: 9, t: 1 },
  );
  acc.add(
    glintSpheroid,
    xform(eyeSide * beam * 0.687, 50, 228, 3.0, 4.5, 3.5),
    [0.72, 0.52, 0.22],
    'eye',
    0.016,
    { group: 9, t: 1 },
  );
  if (closeDetail) {
    // Close inspection earns actual anatomy rather than merely a smoother balloon.
    // A radial maw lives below the normal water crop. It rises only in the attacking
    // deformation state, so the animal does not wear a permanent logo-like face.
    const mawCentre = new Vector3(-38, -72, 272);
    acc.add(
      featureSpheroid,
      xform(mawCentre.x, mawCentre.y, mawCentre.z, 164, 78, 28, 0, 0, -0.08),
      dark,
      'organic',
      0.006,
      { group: 10, t: 1 },
    );
    const mawLobes = closeDetail === 'desktop' ? 16 : 12;
    for (let i = 0; i < mawLobes; i++) {
      const angle = (i / mawLobes) * Math.PI * 2 + 0.05 * Math.sin(i * 1.9);
      const outer = new Vector3(
        mawCentre.x + Math.cos(angle) * 76,
        mawCentre.y + Math.sin(angle) * 34,
        mawCentre.z + 11,
      );
      const inner = new Vector3(
        mawCentre.x + Math.cos(angle) * 20,
        mawCentre.y + Math.sin(angle) * 9,
        mawCentre.z + 25 + (i % 3) * 3,
      );
      acc.add(
        taper(0.28, 1, suckerSides),
        member(outer, inner, 22 + (i % 4) * 2.4),
        i % 3 === 0 ? underside : ridge,
        'organic',
        0.008,
        { group: 10, t: 1 },
      );
    }

    // Broken collars, scars and nodules live where the mantle disappears through the
    // sea. Their uneven heights interrupt the clean toy-like waterline and give foam
    // something anatomical to wrap around.
    const contactNodule = organicSpheroid(8, 5);
    const noduleCount = closeDetail === 'desktop' ? 22 : 14;
    for (let i = 0; i < noduleCount; i++) {
      const angle = -0.45 + (i / Math.max(1, noduleCount - 1)) * Math.PI * 1.42;
      const radius = 178 + (i % 5) * 17;
      acc.add(
        contactNodule,
        xform(
          Math.cos(angle) * radius - 34,
          -18 + (i % 4) * 10,
          Math.sin(angle) * radius + 94,
          22 + (i % 3) * 7,
          13 + (i % 4) * 4,
          36 + (i % 5) * 8,
          angle * 0.12,
          -angle,
          0.14 * Math.sin(i * 1.3),
        ),
        i % 5 === 0 ? litFlesh : flesh,
        'organic',
        0.005,
      );
    }
  }

  // Eight uninterrupted arms, each with a different root and thickness. Only two
  // breach high enough to own the skyline; the others remain low, overlap and disappear
  // through the sea, so the player never sees a complete radial body plan.
  for (let i = 0; i < 8; i++) {
    const path = createKrakenArmPath(i, armSlices);
    const armT: number[] = [];
    for (let slice = 0; slice < path.points.length; slice++) {
      const t = slice / (path.points.length - 1);
      for (let side = 0; side < armSides; side++) armT.push(t);
    }
    // curvedTube appends start and end cap centres after the ring vertices.
    armT.push(0, 1);
    acc.add(
      curvedTube(path.points, path.radii, armSides, true),
      xform(0, 0, 0, 1, 1, 1),
      i % 3 === 0 ? litFlesh : flesh,
      'organic',
      0.0038,
      { group: i + 1, t: armT },
    );
    addKrakenSuckers(acc, path, i, underside, dark, suckerStep, suckerSides);
  }
}

function buildMinisub(acc: HullAccumulator): void {
  const spec = VESSEL_SPECS.minisub;
  const L = spec.length, half = L * 0.5;

  // The pressure hull stays almost entirely below the surface; matte rubber separates it
  // from painted surface ships whenever a wave briefly exposes the casing.
  acc.add(
    taper(0.55, 0.2, 14),
    xform(0, -1.25, half - 3, 3.2, 6, 3.2, 0, Math.PI / 2, 0),
    PALETTE.darkSteel,
    'rubber',
  );
  acc.add(
    cylinder(14),
    xform(0, -1.25, 0, 3.2, L * 0.62, 3.2, 0, Math.PI / 2, 0),
    PALETTE.darkSteel,
    'rubber',
  );
  acc.add(
    taper(0.3, 0.75, 14),
    xform(0, -1.25, -half + 4, 3.2, 7, 3.2, 0, Math.PI / 2, 0),
    PALETTE.darkSteel,
    'rubber',
  );

  // Dive planes occasionally cut the surface and stop the visible casing reading as a
  // plain pipe.
  acc.add(box(), xform(0, -0.25, 4.2, 7.4, 0.22, 1.25), PALETTE.navalDark, 'metal');
  acc.add(box(), xform(0, -1.0, -half + 3.0, 6.0, 0.20, 1.0), PALETTE.navalDark, 'metal');
  acc.add(box(), xform(0, -0.15, -half + 2.8, 0.22, 4.4, 1.1), PALETTE.navalDark, 'metal');

  // A raked sail, periscope crook and snorkel are the deliberate counterplay silhouette.
  acc.add(
    frustum(0.58, 0.62, 0.12),
    xform(0, 0.55, 1.0, 1.55, 2.5, 4.2),
    [0.09, 0.095, 0.10],
    'rubber',
  );
  acc.add(cylinder(7), xform(-0.28, 2.45, 1.6, 0.17, 2.8, 0.17), PALETTE.darkSteel, 'metal');
  acc.add(
    cylinder(6),
    member(new Vector3(-0.28, 3.8, 1.6), new Vector3(-0.28, 3.8, 2.25), 0.15),
    PALETTE.darkSteel,
    'metal',
  );
  acc.add(cylinder(8), xform(0.34, 2.1, 0.65, 0.23, 2.3, 0.23), PALETTE.darkSteel, 'metal');
  acc.add(box(), xform(-0.28, 3.8, 2.28, 0.26, 0.22, 0.18), PALETTE.window, 'glass');

  acc.add(
    cylinder(10),
    xform(0, -1.25, -half + 0.4, 1.5, 0.2, 1.5, 0, Math.PI / 2, 0),
    PALETTE.steel,
    'metal',
  );
}
